import { useState, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import GoogleMap, { type GoogleMapHandle } from '@/components/maps/GoogleMap';
import MapMarker from '@/components/maps/MapMarker';
import GeofenceOverlay from '@/components/maps/GeofenceOverlay';
import { MapPinIcon } from '@heroicons/react/24/outline';
import type { GeographicAnalytics } from '@/services/analytics.service';

/** Reject (0,0), NaN, and out-of-range coordinates */
function isValidCoord(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
}

const RISK_COLORS: Record<string, string> = {
  LOW: '#10B981',
  MEDIUM: '#F59E0B',
  HIGH: '#EF4444',
  CRITICAL: '#DC2626',
};

const FENCE_TYPE_MAP: Record<string, 'SAFE_ZONE' | 'RESTRICTED_ZONE' | 'ALERT_ZONE'> = {
  SAFE_ZONE: 'SAFE_ZONE',
  RESTRICTED_ZONE: 'RESTRICTED_ZONE',
  ALERT_ZONE: 'ALERT_ZONE',
};

interface AnalyticsMapProps {
  data: GeographicAnalytics;
}

const AnalyticsMap: React.FC<AnalyticsMapProps> = ({ data }) => {
  const mapRef = useRef<GoogleMapHandle>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [layers, setLayers] = useState({
    activities: true,
    venues: true,
    geofences: true,
  });

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

  // Filter out malformed coordinates before any rendering
  const ep = useMemo(() => {
    const raw = data.executiveProtection;
    return {
      activities: raw.activities.filter(a => isValidCoord(a.latitude, a.longitude)),
      venues: raw.venues.filter(v => isValidCoord(v.latitude, v.longitude)),
      geofences: raw.geofences.filter(g => isValidCoord(g.centerLatitude, g.centerLongitude)),
    };
  }, [data.executiveProtection]);

  const toggleLayer = (layer: keyof typeof layers) => {
    setLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);

    // Collect all coordinates and fit bounds
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    ep.activities.forEach(a => { bounds.extend({ lat: a.latitude, lng: a.longitude }); hasPoints = true; });
    ep.venues.forEach(v => { bounds.extend({ lat: v.latitude, lng: v.longitude }); hasPoints = true; });
    ep.geofences.forEach(g => { bounds.extend({ lat: g.centerLatitude, lng: g.centerLongitude }); hasPoints = true; });

    if (hasPoints) {
      map.fitBounds(bounds, 50);
    }
  }, [ep]);

  if (!apiKey) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Geographic Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[400px] text-center">
            <MapPinIcon className="w-12 h-12 text-secondary-300 dark:text-secondary-600 mb-3" />
            <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
              Google Maps not configured
            </p>
            <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
              Set VITE_GOOGLE_MAPS_API_KEY to enable the geographic map
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalPoints = ep.activities.length + ep.venues.length + ep.geofences.length;

  if (totalPoints === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Geographic Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[400px] text-center">
            <MapPinIcon className="w-12 h-12 text-secondary-300 dark:text-secondary-600 mb-3" />
            <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
              No geographic data available
            </p>
            <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
              Location data will appear once executive protection activities are recorded
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const layerButtons = [
    { key: 'activities' as const, label: 'Activities', count: ep.activities.length, color: 'bg-red-500' },
    { key: 'venues' as const, label: 'Venues', count: ep.venues.length, color: 'bg-blue-500' },
    { key: 'geofences' as const, label: 'Geofences', count: ep.geofences.length, color: 'bg-green-500' },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle>Geographic Overview</CardTitle>
          <div className="flex flex-wrap gap-2">
            {layerButtons.map(({ key, label, count, color }) => (
              <button
                key={key}
                onClick={() => toggleLayer(key)}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  layers[key]
                    ? 'bg-secondary-100 dark:bg-secondary-700 text-secondary-900 dark:text-white'
                    : 'bg-secondary-50 dark:bg-secondary-800 text-secondary-400 dark:text-secondary-500 line-through'
                }`}
              >
                <span className={`w-2 h-2 rounded-full mr-1.5 ${color} ${!layers[key] ? 'opacity-30' : ''}`} />
                {label} ({count})
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[500px] rounded-lg overflow-hidden">
          <GoogleMap
            ref={mapRef}
            apiKey={apiKey}
            center={{ lat: 39.8283, lng: -98.5795 }}
            zoom={4}
            onMapLoad={handleMapLoad}
            style={{ width: '100%', height: '100%' }}
          >
            {mapInstance && (
              <>
                {/* Activity Markers */}
                {layers.activities && ep.activities.map((activity) => (
                  <MapMarker
                    key={`activity-${activity.id}`}
                    map={mapInstance}
                    position={{ lat: activity.latitude, lng: activity.longitude }}
                    title={activity.name ?? 'Activity'}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      fillColor: RISK_COLORS[activity.riskLevel] ?? RISK_COLORS.MEDIUM,
                      fillOpacity: 1,
                      strokeColor: '#FFFFFF',
                      strokeWeight: 2,
                      scale: 8,
                    }}
                  >
                    <div className="p-2 min-w-[150px]">
                      <p className="font-semibold text-sm">{activity.name ?? 'Activity'}</p>
                      <p className="text-xs text-gray-600">Type: {activity.type}</p>
                      <p className="text-xs text-gray-600">Risk: {activity.riskLevel}</p>
                    </div>
                  </MapMarker>
                ))}

                {/* Venue Markers */}
                {layers.venues && ep.venues.map((venue) => (
                  <MapMarker
                    key={`venue-${venue.id}`}
                    map={mapInstance}
                    position={{ lat: venue.latitude, lng: venue.longitude }}
                    title={venue.name ?? 'Venue'}
                    icon={{
                      path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                      fillColor: '#3B82F6',
                      fillOpacity: 1,
                      strokeColor: '#FFFFFF',
                      strokeWeight: 2,
                      scale: 6,
                    }}
                  >
                    <div className="p-2 min-w-[150px]">
                      <p className="font-semibold text-sm">{venue.name ?? 'Venue'}</p>
                      {venue.address && <p className="text-xs text-gray-600">{venue.address}</p>}
                      {venue.venueType && <p className="text-xs text-gray-600">Type: {venue.venueType}</p>}
                    </div>
                  </MapMarker>
                ))}

                {/* Geofence Overlays */}
                {layers.geofences && ep.geofences.map((geofence) => (
                  <GeofenceOverlay
                    key={`geofence-${geofence.id}`}
                    map={mapInstance}
                    center={{ lat: geofence.centerLatitude, lng: geofence.centerLongitude }}
                    radius={geofence.radiusMeters}
                    type={FENCE_TYPE_MAP[geofence.fenceType] || undefined}
                  />
                ))}

              </>
            )}
          </GoogleMap>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsMap;
